import type { TradeItem, View, PositionsSummarySections } from '@/types';

/**
 * Count, per view, how many trades fall into it.
 *
 * A view with an empty `counterparties` array (e.g. All Trades) counts the
 * whole list; a per-counterparty view counts trades whose `counterparty`
 * matches any name in `view.counterparties`.
 *
 * Implementation note: builds a `counterparty -> viewId[]` map up front, then
 * walks the trade list once and bumps the matching views. Complexity is
 * O(trades + views + Σ view.counterparties.length) instead of O(views × trades)
 * (kvi-r1 #5). For the typical view set (one System view + N auto-generated
 * per-CP views) Σ counterparties ≈ N, so the work scales with active CPs and
 * row volume, not their product.
 */
export function computeViewTradeCounts(
  trades: readonly TradeItem[],
  views: readonly View[],
): Record<string, number> {
  const counts: Record<string, number> = {};
  const cpToViewIds = new Map<string, string[]>();
  const allTradesViewIds: string[] = [];

  for (const view of views) {
    counts[view.id] = 0;
    if (view.counterparties.length === 0) {
      allTradesViewIds.push(view.id);
    } else {
      for (const cp of view.counterparties) {
        const arr = cpToViewIds.get(cp);
        if (arr) arr.push(view.id);
        else cpToViewIds.set(cp, [view.id]);
      }
    }
  }

  for (const id of allTradesViewIds) counts[id] = trades.length;

  for (const trade of trades) {
    const matchingViewIds = cpToViewIds.get(trade.counterparty);
    if (!matchingViewIds) continue;
    for (const id of matchingViewIds) counts[id]++;
  }

  return counts;
}

/** A trade of a counterparty view: by the tab's id when it has one (the name it stored can
 *  trail a rename), else by name. */
export const isTradeInView = (trade: TradeItem, view: View): boolean =>
  view.counterpartyId !== undefined
    ? trade.counterpartyId === view.counterpartyId
    : view.counterparties.includes(trade.counterparty);

/**
 * LR-546: per-view tab counts derived from the server metrics aggregate
 * (`GET /positions/metrics/`) instead of the in-memory book. An All-Trades view
 * (empty `counterparties`) shows the overall count; a per-CP view sums its
 * counterparties' `count`, a tab with an id reads its own (by id, as `isTradeInView`).
 * `summary` undefined (loading or failed) → all zeros; the tab bar shows neither as a count.
 */
export function computeViewCountsFromSummary(
  summary: PositionsSummarySections['counts'] | undefined,
  views: readonly View[],
): Record<string, number> {
  const cpCount = new Map<string, number>();
  const idCount = new Map<number, number>();
  for (const e of summary?.by_counterparty ?? []) {
    if (e.counterparty_name) cpCount.set(e.counterparty_name, e.count);
    if (e.counterparty_id !== null) idCount.set(e.counterparty_id, e.count);
  }
  const overall = summary?.overall.count ?? 0;
  const counts: Record<string, number> = {};
  for (const view of views) {
    counts[view.id] = view.counterparties.length === 0
      ? overall
      : view.counterpartyId !== undefined
        ? (idCount.get(view.counterpartyId) ?? 0)
        : view.counterparties.reduce((acc, cp) => acc + (cpCount.get(cp) ?? 0), 0);
  }
  return counts;
}
